import { QueryFunctionContext } from "@tanstack/react-query";

import {
  BROCreateRoutesPayload,
  BRODraftPayload,
  BROJobListItem,
  BROJobResponse,
  BroResponse,
  DriverRouteType,
  Route,
  RouteDraft,
  RoutePreviewPayload,
  RoutePreviewResponse,
  createRoutePayloadType,
  updateRoutePayloadV2Type,
} from "@web-app/types";
import http from "@web-app/utils/http";

const create = async (payload: createRoutePayloadType) => {
  return http.post<Route>("/routes", payload).then((response) => response.data);
};

const createDraft = async (payload: createRoutePayloadType) => {
  return http.post<RouteDraft>("/routes/draft", payload).then((response) => {
    const routeData = response.data;

    const routeOrdersAfterDelayCalculation = routeData.routeOrders?.map(
      (order) => {
        const orderEndTime = new Date(order.scheduledEndDateTime).getTime();
        const estimatedEndTime = new Date(
          order.eta?.dateTime as Date,
        ).getTime();

        if (orderEndTime < estimatedEndTime) {
          return { ...order, isDelayed: true };
        }

        return { ...order, isDelayed: false };
      },
    );

    const data = {
      ...routeData,
      routeOrders: routeOrdersAfterDelayCalculation,
    };

    return data;
  });
};

const updateV2 = async (payload: updateRoutePayloadV2Type) => {
  return http
    .patch<Route>(`/routesv2/${payload.routeId}`, payload)
    .then((response) => response.data);
};

const remove = async (routeId: string) => {
  return http.delete(`/routes/${routeId}`);
};

// Single-route compute/optimize preview from raw orders. No DB writes.
const previewRoute = async (payload: RoutePreviewPayload) => {
  return http
    .post<RoutePreviewResponse>("/routes/preview", payload)
    .then((response) => response.data);
};

const createAsyncDraftJob = async (payload: BRODraftPayload) => {
  return http
    .post<{ jobId: string }>("/routes/bulk/asyncDraftJob", payload)
    .then((response) => response.data);
};

const getBROJob = async (jobId: string) => {
  return http
    .get<BROJobResponse>(`/routes/bulk/asyncDraftJob/${jobId}`)
    .then((response) => response.data);
};

const updateBROJob = async (jobId: string, payload: BRODraftPayload) => {
  return http.patch(`/routes/bulk/asyncDraftJob/${jobId}`, payload);
};

// Overwrite a saved job's drafts with the current edits (no re-optimization).
const saveBRODraft = async (jobId: string, payload: BroResponse) => {
  return http.patch(`/routes/bulk/asyncDraftJob/${jobId}/draft`, payload);
};

const deleteAsyncDraftJob = async (jobId: string) => {
  return http.delete(`/routes/bulk/asyncDraftJob/${jobId}`);
};

const getAsyncDraftJobs = async () => {
  return http
    .get<BROJobListItem[]>("/routes/bulk/asyncDraftJobs")
    .then((response) => response.data);
};

const createBulkRoutes = async (payload: BROCreateRoutesPayload) => {
  return http.post("/routes/bulk/create", payload);
};

const getRouteDriverRoute = async ({
  queryKey,
  signal,
}: QueryFunctionContext) => {
  const [, routeId] = queryKey as [string, string];
  return http
    .get<DriverRouteType>(`routes/${routeId}/driverRoute`, {
      signal,
    })
    .then((res) => {
      return res.data;
    });
};

export interface CopyRoutePayload {
  newScheduledDate: string;
}

const copy = async (routeId: string, payload: CopyRoutePayload) => {
  return http
    .post<Route>(`/routes/${routeId}/copy`, payload)
    .then((response) => response.data);
};

export {
  copy,
  create,
  createAsyncDraftJob,
  createBulkRoutes,
  createDraft,
  deleteAsyncDraftJob,
  getAsyncDraftJobs,
  getBROJob,
  getRouteDriverRoute,
  previewRoute,
  remove,
  saveBRODraft,
  updateBROJob,
  updateV2,
};
